import React, { useState, useEffect } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import queryString from "query-string";
import Header from "../../../Header";
import Footer from "../../../Footer";
import { checkIsAdminLoggined } from "../../../../utils/utils.js";
import axios from "axios";
import ReactPaginate from "react-paginate";
import { Helmet } from "react-helmet";
import { Col, Row, Button, Badge } from "react-bootstrap";
import NumberFormat from "react-number-format";
import "../../../../styles/admin-users.css";

const AdminUsersPage = props => {
  const token = localStorage.getItem("token");
  const history = useHistory();
  const location = useLocation();

  // Read URL parameters on mount
  const urlParams = queryString.parse(location.search);

  const [users, setUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(() => {
    const page = urlParams.page ? parseInt(urlParams.page) : 1;
    return page - 1;
  });
  const [numberPerPage, setNumberPerPage] = useState(20);
  const [totalNumber, setTotalNumber] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Filters - initialize from URL
  const [userTypeFilter, setUserTypeFilter] = useState(urlParams.userType || "");
  const [discountLevelFilter, setDiscountLevelFilter] = useState(urlParams.discountLevel || "");
  const [hasOrdersFilter, setHasOrdersFilter] = useState(urlParams.hasOrders || "");
  const [registrationDateFromFilter, setRegistrationDateFromFilter] = useState(urlParams.registrationDateFrom?.split("T")[0] || "");
  const [registrationDateToFilter, setRegistrationDateToFilter] = useState(urlParams.registrationDateTo?.split("T")[0] || "");
  const [userIdFilter, setUserIdFilter] = useState(urlParams.userId || "");
  const [searchFilter, setSearchFilter] = useState(urlParams.search || "");
  const [sortBy, setSortBy] = useState(urlParams.sortBy || "registrationDate");
  const [sortOrder, setSortOrder] = useState(urlParams.sortOrder || "DESC");

  // Discount levels for filter dropdown
  const [discountLevelsList, setDiscountLevelsList] = useState([]);

  // Create user modal state
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [newUserPhoneNumber, setNewUserPhoneNumber] = useState("");
  const [createUserError, setCreateUserError] = useState("");
  const [createUserSuccess, setCreateUserSuccess] = useState("");
  const [isCreatingUser, setIsCreatingUser] = useState(false);

  // Fill names from orders job
  const [fillNamesLoading, setFillNamesLoading] = useState(false);
  const [fillNamesMessage, setFillNamesMessage] = useState("");
  const [fillNamesIsError, setFillNamesIsError] = useState(false);

  // Auto-hide fill-names message after 5 seconds
  useEffect(() => {
    if (!fillNamesMessage) return;
    const t = setTimeout(() => {
      setFillNamesMessage("");
      setFillNamesIsError(false);
    }, 5000);
    return () => clearTimeout(t);
  }, [fillNamesMessage]);

  // Edit name/surname modal (list)
  const [showEditNameModal, setShowEditNameModal] = useState(false);
  const [editNameUser, setEditNameUser] = useState(null);
  const [editNameName, setEditNameName] = useState("");
  const [editNameSurname, setEditNameSurname] = useState("");
  const [editNameSaving, setEditNameSaving] = useState(false);
  const [editNameError, setEditNameError] = useState("");

  // Function to update URL with current filters and pagination
  const updateURL = (page = currentPage) => {
    const params = {};
    if (userTypeFilter) params.userType = userTypeFilter;
    if (discountLevelFilter) params.discountLevel = discountLevelFilter;
    if (hasOrdersFilter !== "") params.hasOrders = hasOrdersFilter === "true";
    if (registrationDateFromFilter) params.registrationDateFrom = registrationDateFromFilter;
    if (registrationDateToFilter) params.registrationDateTo = registrationDateToFilter;
    if (userIdFilter) params.userId = userIdFilter;
    if (searchFilter) params.search = searchFilter;
    if (sortBy) params.sortBy = sortBy;
    if (sortOrder) params.sortOrder = sortOrder;
    if (page >= 0) params.page = page + 1;

    const query = queryString.stringify(params);
    const newUrl = `/admin/users${query ? `?${query}` : ''}`;
    if (location.pathname + location.search !== newUrl) {
      history.push(newUrl);
    }
  };

  useEffect(() => {
    checkIsAdminLoggined(props);
    window.scrollTo(0, 0);
    loadUsers(currentPage);
  }, []);

  useEffect(() => {
    axios
      .get('/api/v1/user/discount-level', { headers: { Authorization: token } })
      .then(res => {
        if (res.data && res.data.data) setDiscountLevelsList(res.data.data);
      })
      .catch(() => {});
  }, []);

  // Reset page to 0 when filters change (but not when currentPage or numberPerPage changes)
  useEffect(() => {
    setCurrentPage(0);
  }, [userTypeFilter, discountLevelFilter, hasOrdersFilter, registrationDateFromFilter, registrationDateToFilter, userIdFilter, searchFilter, sortBy, sortOrder]);

  // Load users and update URL when filters or pagination changes
  useEffect(() => {
    updateURL(currentPage);
    loadUsers(currentPage);
  }, [currentPage, numberPerPage, userTypeFilter, discountLevelFilter, hasOrdersFilter, registrationDateFromFilter, registrationDateToFilter, userIdFilter, searchFilter, sortBy, sortOrder]);

  const loadUsers = (page) => {
    setIsLoading(true);
    const params = new URLSearchParams();
    if (userTypeFilter) params.append("userType", userTypeFilter);
    if (discountLevelFilter) params.append("discountLevel", discountLevelFilter);
    if (hasOrdersFilter !== "") params.append("hasOrders", hasOrdersFilter === "true");
    if (registrationDateFromFilter) params.append("registrationDateFrom", registrationDateFromFilter + "T00:00:00");
    if (registrationDateToFilter) params.append("registrationDateTo", registrationDateToFilter + "T23:59:59");
    if (userIdFilter) params.append("userId", userIdFilter);
    if (searchFilter) params.append("search", searchFilter);
    if (sortBy) params.append("sortBy", sortBy);
    if (sortOrder) params.append("sortOrder", sortOrder);
    params.append("page", (page || currentPage) + 1);
    params.append("numberPerPage", numberPerPage);

    axios
      .get(`/api/v1/admin/users?${params.toString()}`, {
        headers: {
          Authorization: token,
        },
      })
      .then(res => {
        if (res.status === 200 && res.data.data) {
          setUsers(res.data.data);
          setTotalNumber(res.data.totalNumber || 0);
          setPageCount(Math.ceil((res.data.totalNumber || 0) / numberPerPage));
        }
        setIsLoading(false);
      })
      .catch(error => {
        console.error("Error loading users:", error);
        setIsLoading(false);
      });
  };

  const handlePageClick = (data) => {
    const page = data.selected;
    setCurrentPage(page);
    updateURL(page);
    window.scrollTo(0, 0);
  };

  const handleSearch = () => {
    setCurrentPage(0);
    updateURL(0);
    loadUsers(0);
  };

  const handleResetFilters = () => {
    setUserTypeFilter("");
    setDiscountLevelFilter("");
    setHasOrdersFilter("");
    setRegistrationDateFromFilter("");
    setRegistrationDateToFilter("");
    setUserIdFilter("");
    setSearchFilter("");
    setSortBy("registrationDate");
    setSortOrder("DESC");
    setCurrentPage(0);
    history.push('/admin/users');
  };

  const handleFillNamesFromOrders = () => {
    setFillNamesMessage("");
    setFillNamesIsError(false);
    setFillNamesLoading(true);
    axios
      .post("/api/v1/user/fill-names-from-orders", {}, { headers: { Authorization: token } })
      .then((res) => {
        const msg = res.data && res.data.data != null ? res.data.data : "Готово";
        setFillNamesMessage(msg);
        setFillNamesIsError(false);
        loadUsers(currentPage);
      })
      .catch((err) => {
        const msg = err.response && err.response.data && err.response.data.errorMessage
          ? err.response.data.errorMessage
          : "Помилка виконання";
        setFillNamesMessage(msg);
        setFillNamesIsError(true);
      })
      .finally(() => setFillNamesLoading(false));
  };

  const openEditNameModal = (user) => {
    setEditNameUser(user);
    setEditNameName(user.name || "");
    setEditNameSurname(user.surname || "");
    setEditNameError("");
    setShowEditNameModal(true);
  };

  const handleSaveEditName = () => {
    if (!editNameUser) return;
    setEditNameSaving(true);
    setEditNameError("");
    axios
      .put(
        `/api/v1/admin/users/${editNameUser.id}`,
        { name: editNameName.trim() || null, surname: editNameSurname.trim() || null },
        { headers: { Authorization: token } }
      )
      .then(() => {
        setShowEditNameModal(false);
        setEditNameUser(null);
        loadUsers(currentPage);
      })
      .catch((err) => {
        setEditNameError(err.response?.data?.errorMessage || "Помилка збереження");
      })
      .finally(() => setEditNameSaving(false));
  };

  const formatCurrency = (amount) => {
    if (!amount) return "0грн";
    return `${amount.toLocaleString("uk-UA")}грн`;
  };

  const handleCreateUser = () => {
    if (!newUserPhoneNumber || newUserPhoneNumber.length !== 10) {
      setCreateUserError("Введіть коректний номер телефону (10 цифр)");
      return;
    }

    setIsCreatingUser(true);
    setCreateUserError("");
    setCreateUserSuccess("");

    axios
      .post(
        "/api/v1/admin/users",
        { phoneNumber: newUserPhoneNumber },
        {
          headers: {
            Authorization: token,
          },
        }
      )
      .then(res => {
        if (res.status === 200) {
          setCreateUserSuccess(res.data.data || "Користувача успішно створено");
          setNewUserPhoneNumber("");
          loadUsers(currentPage);
          setTimeout(() => {
            setShowCreateUserModal(false);
            setCreateUserSuccess("");
          }, 2000);
        }
        setIsCreatingUser(false);
      })
      .catch(error => {
        const res = error.response;
        if (res && res.data && res.data.errorMessage) {
          setCreateUserError(res.data.errorMessage);
        } else {
          setCreateUserError("Помилка при створенні користувача");
        }
        setIsCreatingUser(false);
      });
  };

  const handleCloseCreateUserModal = () => {
    setShowCreateUserModal(false);
    setNewUserPhoneNumber("");
    setCreateUserError("");
    setCreateUserSuccess("");
  };

  const handlePhoneNumberChange = (value) => {
    setNewUserPhoneNumber(value);
    setCreateUserError("");
  };

  const handleSortClick = (field) => {
    if (sortBy === field) {
      // Якщо клікнули на той самий заголовок - змінюємо порядок
      setSortOrder(sortOrder === "ASC" ? "DESC" : "ASC");
    } else {
      // Якщо клікнули на інший заголовок - встановлюємо нове поле і DESC за замовчуванням
      setSortBy(field);
      setSortOrder("DESC");
    }
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) {
      return null; // Не показуємо іконку, якщо не сортуємо по цьому полю
    }
    return sortOrder === "ASC" ? (
      <i className="bi bi-arrow-up ml-1" style={{ fontSize: '0.875rem' }}></i>
    ) : (
      <i className="bi bi-arrow-down ml-1" style={{ fontSize: '0.875rem' }}></i>
    );
  };

  return (
    <div className="container admin-users-page">
      <Helmet>
        <title>Адмін панель - Користувачі</title>
      </Helmet>
      <div className="row">
        <div className="col p-0 pb-3">
          <Header />
        </div>
      </div>

      {/* Filters */}
      <div className="row filters">
        <div className="col-1 py-1 px-1">
          <div>
            <p className="mb-1 font-weight-bold" style={{ fontSize: '0.875rem' }}>ID:</p>
            <input
              type="number"
              className="form-control"
              min="1"
              value={userIdFilter}
              onChange={(e) => setUserIdFilter(e.target.value)}
              placeholder="ID"
              style={{ fontSize: '0.875rem' }}
            />
          </div>
        </div>
        <div className="col-2 py-1 px-1">
          <div>
            <p className="mb-1 font-weight-bold" style={{ fontSize: '0.875rem' }}>Тип користувача:</p>
            <select
              className="form-control"
              value={userTypeFilter}
              onChange={(e) => setUserTypeFilter(e.target.value)}
              style={{ fontSize: '0.875rem' }}
            >
              <option value="">Всі</option>
              <option value="USER">USER</option>
              <option value="ADMIN">ADMIN</option>
              <option value="SUPER_USER">SUPER_USER</option>
            </select>
          </div>
        </div>
        <div className="col-2 py-1 px-1">
          <div>
            <p className="mb-1 font-weight-bold" style={{ fontSize: '0.875rem' }}>Рівень знижки:</p>
            <select
              className="form-control"
              value={discountLevelFilter}
              onChange={(e) => setDiscountLevelFilter(e.target.value)}
              style={{ fontSize: '0.875rem' }}
            >
              <option value="">Всі</option>
              {discountLevelsList.map((l) => (
                <option key={l.level} value={String(l.level)}>
                  {l.level} ({l.levelName || ''})
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="col-2 py-1 px-1">
          <div>
            <p className="mb-1 font-weight-bold" style={{ fontSize: '0.875rem' }}>Має замовлення:</p>
            <select
              className="form-control"
              value={hasOrdersFilter}
              onChange={(e) => setHasOrdersFilter(e.target.value)}
              style={{ fontSize: '0.875rem' }}
            >
              <option value="">Всі</option>
              <option value="true">Так</option>
              <option value="false">Ні</option>
            </select>
          </div>
        </div>
        <div className="col-2 py-1 px-1">
          <div>
            <p className="mb-1 font-weight-bold" style={{ fontSize: '0.875rem' }}>Дата реєстрації від:</p>
            <input
              type="date"
              className="form-control"
              value={registrationDateFromFilter}
              onChange={(e) => setRegistrationDateFromFilter(e.target.value)}
              style={{ fontSize: '0.875rem' }}
            />
          </div>
        </div>
        <div className="col-2 py-1 px-1">
          <div>
            <p className="mb-1 font-weight-bold" style={{ fontSize: '0.875rem' }}>Дата реєстрації до:</p>
            <input
              type="date"
              className="form-control"
              value={registrationDateToFilter}
              onChange={(e) => setRegistrationDateToFilter(e.target.value)}
              style={{ fontSize: '0.875rem' }}
            />
          </div>
        </div>
        <div className="col-1 py-1 px-1">
        </div>
      </div>
      <div className="row filters mb-3">
        <div className="col-12 py-1 px-1">
          <p className="mb-1 font-weight-bold" style={{ fontSize: '0.875rem' }}>Пошук (телефон, email):</p>
          <div className="position-relative d-flex align-items-center">
            <input
              type="text"
              className="form-control pr-4"
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
              placeholder="Введіть телефон або email"
              style={{ fontSize: '0.875rem' }}
            />
            {searchFilter && (
              <button
                type="button"
                className="btn p-0 border-0 bg-transparent position-absolute"
                style={{ right: '8px', fontSize: '1.1rem', lineHeight: 1, color: '#6c757d', cursor: 'pointer' }}
                onClick={() => setSearchFilter('')}
                title="Очистити пошук"
                aria-label="Очистити пошук"
              >
                <i className="bi bi-x"></i>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Active Filters and Action Buttons */}
      <div className="row">
        <div className="col-12 p-0">
          <div className="mb-3 d-flex justify-content-between align-items-start flex-wrap" style={{ gap: '0.5rem' }}>
            {/* Active Filters */}
            <div className="d-flex flex-wrap align-items-center" style={{ gap: '0.5rem', flex: '1', minWidth: '0' }}>
              {(userIdFilter || userTypeFilter || discountLevelFilter || hasOrdersFilter || registrationDateFromFilter || registrationDateToFilter || searchFilter) ? (
                <>
                  <span className="font-weight-bold" style={{ fontSize: '0.875rem' }}>Активні фільтри:</span>
              {userIdFilter && (
                <span className="badge badge-dark d-inline-flex align-items-center" style={{ fontSize: '0.875rem', padding: '0.35rem 0.5rem', fontWeight: 'bold' }}>
                  ID: {userIdFilter}
                  <button
                    type="button"
                    className="btn p-0 ml-2 border-0 bg-transparent"
                    onClick={() => setUserIdFilter('')}
                    title="Скинути фільтр"
                    style={{ fontSize: '0.875rem', color: 'inherit', lineHeight: '1', padding: '0', cursor: 'pointer', opacity: '0.8' }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '0.8'}
                  >
                    <i className="bi bi-x" style={{ fontWeight: 'bold' }}></i>
                  </button>
                </span>
              )}
              {userTypeFilter && (
                <span className="badge badge-dark d-inline-flex align-items-center" style={{ fontSize: '0.875rem', padding: '0.35rem 0.5rem', fontWeight: 'bold' }}>
                  Тип: {userTypeFilter}
                  <button
                    type="button"
                    className="btn p-0 ml-2 border-0 bg-transparent"
                    onClick={() => setUserTypeFilter('')}
                    title="Скинути фільтр"
                    style={{ fontSize: '0.875rem', color: 'inherit', lineHeight: '1', padding: '0', cursor: 'pointer', opacity: '0.8' }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '0.8'}
                  >
                    <i className="bi bi-x" style={{ fontWeight: 'bold' }}></i>
                  </button>
                </span>
              )}
              {discountLevelFilter && (() => {
                const levelInfo = discountLevelsList.find((l) => String(l.level) === String(discountLevelFilter));
                const label = levelInfo ? `${levelInfo.level} (${levelInfo.levelName || ''})` : discountLevelFilter;
                return (
                <span className="badge badge-dark d-inline-flex align-items-center" style={{ fontSize: '0.875rem', padding: '0.35rem 0.5rem', fontWeight: 'bold' }}>
                  Рівень знижки: {label}
                  <button
                    type="button"
                    className="btn p-0 ml-2 border-0 bg-transparent"
                    onClick={() => setDiscountLevelFilter('')}
                    title="Скинути фільтр"
                    style={{ fontSize: '0.875rem', color: 'inherit', lineHeight: '1', padding: '0', cursor: 'pointer', opacity: '0.8' }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '0.8'}
                  >
                    <i className="bi bi-x" style={{ fontWeight: 'bold' }}></i>
                  </button>
                </span>
                );
              })()}
              {hasOrdersFilter && (
                <span className="badge badge-dark d-inline-flex align-items-center" style={{ fontSize: '0.875rem', padding: '0.35rem 0.5rem', fontWeight: 'bold' }}>
                  Має замовлення: {hasOrdersFilter === 'true' ? 'Так' : 'Ні'}
                  <button
                    type="button"
                    className="btn p-0 ml-2 border-0 bg-transparent"
                    onClick={() => setHasOrdersFilter('')}
                    title="Скинути фільтр"
                    style={{ fontSize: '0.875rem', color: 'inherit', lineHeight: '1', padding: '0', cursor: 'pointer', opacity: '0.8' }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '0.8'}
                  >
                    <i className="bi bi-x" style={{ fontWeight: 'bold' }}></i>
                  </button>
                </span>
              )}
              {registrationDateFromFilter && (
                <span className="badge badge-dark d-inline-flex align-items-center" style={{ fontSize: '0.875rem', padding: '0.35rem 0.5rem', fontWeight: 'bold' }}>
                  Дата від: {registrationDateFromFilter}
                  <button
                    type="button"
                    className="btn p-0 ml-2 border-0 bg-transparent"
                    onClick={() => setRegistrationDateFromFilter('')}
                    title="Скинути фільтр"
                    style={{ fontSize: '0.875rem', color: 'inherit', lineHeight: '1', padding: '0', cursor: 'pointer', opacity: '0.8' }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '0.8'}
                  >
                    <i className="bi bi-x" style={{ fontWeight: 'bold' }}></i>
                  </button>
                </span>
              )}
              {registrationDateToFilter && (
                <span className="badge badge-dark d-inline-flex align-items-center" style={{ fontSize: '0.875rem', padding: '0.35rem 0.5rem', fontWeight: 'bold' }}>
                  Дата до: {registrationDateToFilter}
                  <button
                    type="button"
                    className="btn p-0 ml-2 border-0 bg-transparent"
                    onClick={() => setRegistrationDateToFilter('')}
                    title="Скинути фільтр"
                    style={{ fontSize: '0.875rem', color: 'inherit', lineHeight: '1', padding: '0', cursor: 'pointer', opacity: '0.8' }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '0.8'}
                  >
                    <i className="bi bi-x" style={{ fontWeight: 'bold' }}></i>
                  </button>
                </span>
              )}
              {searchFilter && (
                <span className="badge badge-dark d-inline-flex align-items-center" style={{ fontSize: '0.875rem', padding: '0.35rem 0.5rem', fontWeight: 'bold' }}>
                  Пошук: {searchFilter}
                  <button
                    type="button"
                    className="btn p-0 ml-2 border-0 bg-transparent"
                    onClick={() => setSearchFilter('')}
                    title="Скинути фільтр"
                    style={{ fontSize: '0.875rem', color: 'inherit', lineHeight: '1', padding: '0', cursor: 'pointer', opacity: '0.8' }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '0.8'}
                  >
                    <i className="bi bi-x" style={{ fontWeight: 'bold' }}></i>
                  </button>
                </span>
              )}
                </>
              ) : null}
            </div>
            {/* Action Buttons */}
            <div className="d-flex align-items-center flex-wrap" style={{ flexShrink: 0, gap: '0.5rem' }}>
              <button className="btn btn-danger" onClick={handleResetFilters} style={{ fontSize: '0.875rem' }}>
                Очистити фільтр та сортування
              </button>
              <button className="btn btn-dark" onClick={handleSearch} style={{ fontSize: '0.875rem' }}>
                Оновити
              </button>
              <button className="btn btn-success" onClick={() => setShowCreateUserModal(true)} style={{ fontSize: '0.875rem' }}>
                Додати користувача
              </button>
            </div>
          </div>
        </div>
      </div>
      <div style={{ marginLeft: '-15px', marginRight: '-15px' }}>
        <div className="d-flex align-items-center mb-1" style={{ gap: '0.5rem' }}>
          <button
            className="btn btn-dark"
            onClick={handleFillNamesFromOrders}
            disabled={fillNamesLoading}
            title="Заповнити імʼя та прізвище у користувачів з порожніми полями, взявши дані з їхніх замовлень"
            style={{ fontSize: '0.875rem' }}
          >
            {fillNamesLoading ? "Виконання…" : "Заповнити імена з замовлень"}
          </button>
        </div>

        {fillNamesMessage && (
          <div className={`alert text-center mb-2 mt-1 px-0 ${fillNamesIsError ? "alert-danger" : "alert-success"}`} role="alert">
            {fillNamesMessage}
          </div>
        )}
      </div>

      <h5 className="text-center font-weight-bold">Всього користувачів: {totalNumber}</h5>
      <div className="row py-3">
        <div className="col-1 pl-0 font-weight-bold">ID</div>
        <div className="col-2 font-weight-bold">Клієнт</div>
        <div className="col-2 font-weight-bold">Email</div>
        <div className="col-2 font-weight-bold text-center">Рівень знижки</div>
        <div 
          className="col-2 font-weight-bold" 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleSortClick('registrationDate')}
        >
          Дата реєстрації{getSortIcon('registrationDate')}
        </div>
        <div 
          className="col-1 font-weight-bold" 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleSortClick('ordersCount')}
        >
          Замовлень{getSortIcon('ordersCount')}
        </div>
        <div 
          className="col-1 font-weight-bold" 
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => handleSortClick('totalRevenue')}
        >
          Витрачено{getSortIcon('totalRevenue')}
        </div>
        <div className="col-1 pr-0 font-weight-bold">Дії</div>
        <div className="col-12 p-0 pt-2">
          <hr className="h-divider" />
        </div>
      </div>
      {isLoading ? (
        <div className="text-center p-4">
          <p>Завантаження...</p>
        </div>
      ) : (
        <>
          {users !== undefined && users !== null
            ? users.map((user, i) => {
                return (
                  <div key={i}>
                    <div className="user-content">
                      <div className="row">
                        <div className="col-1 pl-0">
                          <p className="p-0 m-0 font-weight-bold">#{user.id}</p>
                        </div>
                        <div className="col-2">
                          <p className="p-0 m-0 d-flex align-items-center flex-wrap">
                            {user.name || user.surname ? (
                              <>
                                <span>{[user.name, user.surname].filter(Boolean).map(s => s.trim()).join(" ")}</span>
                                <button
                                  type="button"
                                  className="btn btn-link btn-sm p-0 ml-1 text-dark"
                                  onClick={() => openEditNameModal(user)}
                                  title="Редагувати ім'я та прізвище"
                                  aria-label="Редагувати ім'я та прізвище"
                                >
                                  <i className="bi bi-pencil-fill text-dark" style={{ fontSize: "0.9rem" }} />
                                </button>
                              </>
                            ) : (
                              <>
                                <span className="text-muted">-</span>
                                <button
                                  type="button"
                                  className="btn btn-link btn-sm p-0 ml-1 text-dark"
                                  onClick={() => openEditNameModal(user)}
                                  title="Додати ім'я та прізвище"
                                  aria-label="Додати ім'я та прізвище"
                                >
                                  <i className="bi bi-pencil-fill text-dark" style={{ fontSize: "0.9rem" }} />
                                </button>
                              </>
                            )}
                          </p>
                          <p className="p-0 m-0">
                            <small>
                              <i className="bi bi-telephone-fill pr-1" />
                              <span className="font-weight-bold">{user.phone || <span className="text-muted">-</span>}</span>
                            </small>
                          </p>
                        </div>
                        <div className="col-2">
                          <p className="p-0 m-0">{user.email || <span className="text-muted">-</span>}</p>
                        </div>
                        <div className="col-2">
                          <p className="p-0 m-0 text-center">
                            {(() => {
                              const autoLevel = user.discountLevel ?? 0;
                              const manualLevel = user.manualDiscountLevel ?? 0;
                              const effectiveLevel = Math.max(autoLevel, manualLevel);
                              const effectiveName = effectiveLevel === manualLevel && user.manualDiscountLevelName
                                ? user.manualDiscountLevelName
                                : (user.discountLevelName || null);
                              return effectiveName
                                ? `${effectiveLevel} (${effectiveName})`
                                : String(effectiveLevel);
                            })()}
                          </p>
                        </div>
                        <div className="col-2">
                          <p className="p-0 m-0"><small>{user.registrationDate || <span className="text-muted">-</span>}</small></p>
                        </div>
                        <div className="col-1">
                          <p className="p-0 m-0">
                            {user.hasOrders ? (
                              <Badge variant="success">{user.ordersCount || 0}</Badge>
                            ) : (
                              <span className="text-muted">0</span>
                            )}
                          </p>
                        </div>
                        <div className="col-1">
                          <p className="p-0 m-0">
                            <small className="font-weight-bold">{formatCurrency(user.totalSpent || 0)}</small>
                          </p>
                        </div>
                        <div className="col-1 pr-0">
                          <Link to={`/admin/users/${user.id}`} className="btn btn-sm btn-dark">
                            Деталі
                          </Link>
                        </div>
                      </div>
                    </div>
                    {i < users.length - 1 && (
                      <div className="row">
                        <div className="col-12">
                          <hr style={{ margin: "0.5rem 0", borderTop: "1px solid #dee2e6" }} />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            : null}

          {users.length === 0 && !isLoading && (
            <div className="text-center p-4">
              <p>Користувачів не знайдено</p>
            </div>
          )}

          {pageCount > 1 && (
            <div className="d-flex justify-content-center mt-3">
              <ReactPaginate
                previousLabel={"‹"}
                nextLabel={"›"}
                breakLabel={"..."}
                breakClassName={"break-me"}
                pageCount={pageCount}
                marginPagesDisplayed={2}
                pageRangeDisplayed={5}
                onPageChange={handlePageClick}
                containerClassName={"pagination"}
                subContainerClassName={"pages pagination"}
                activeClassName={"active"}
                forcePage={currentPage}
              />
            </div>
          )}
        </>
      )}

      {/* Create User Modal — Bootstrap classes only */}
      {showCreateUserModal && (
        <>
          <div className="modal-backdrop show" style={{ zIndex: 1040 }} onClick={handleCloseCreateUserModal} />
          <div
            className="modal show"
            tabIndex={-1}
            role="dialog"
            style={{ display: "block", zIndex: 1041 }}
            onClick={handleCloseCreateUserModal}
          >
            <div className="modal-dialog modal-dialog-centered" role="document" onClick={e => e.stopPropagation()}>
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Додати нового користувача</h5>
                  <button type="button" className="close" aria-label="Close" onClick={handleCloseCreateUserModal}>
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
                <div className="modal-body">
                  <div className="form-group">
                    <label>Номер телефону:</label>
                    <NumberFormat
                      className="form-control"
                      placeholder="Введіть номер телефону"
                      format="(###) ###-####"
                      mask="_"
                      value={newUserPhoneNumber}
                      onValueChange={({ value }) => handlePhoneNumberChange(value)}
                      disabled={isCreatingUser}
                    />
                    <small className="form-text text-muted">
                      Формат: 10 цифр без коду країни (наприклад: 0501234567)
                    </small>
                  </div>
                  {createUserError && (
                    <div className="alert alert-danger mt-2" role="alert">
                      {createUserError}
                    </div>
                  )}
                  {createUserSuccess && (
                    <div className="alert alert-success mt-2" role="alert">
                      {createUserSuccess}
                    </div>
                  )}
                  <p className="text-muted mt-3 mb-0">
                    Користувач буде зареєстрований без пароля. Для входу на сайт користувач може використати функцію "Відновити пароль" за номером телефону
                  </p>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={handleCloseCreateUserModal} disabled={isCreatingUser}>
                    Скасувати
                  </button>
                  <button type="button" className="btn btn-success" onClick={handleCreateUser} disabled={isCreatingUser}>
                    {isCreatingUser ? "Створення..." : "Створити"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Edit name/surname modal — Bootstrap classes only */}
      {showEditNameModal && editNameUser && (
        <>
          <div className="modal-backdrop show" style={{ zIndex: 1040 }} onClick={() => !editNameSaving && setShowEditNameModal(false)} />
          <div
            className="modal show"
            tabIndex={-1}
            role="dialog"
            style={{ display: "block", zIndex: 1041 }}
            onClick={() => !editNameSaving && setShowEditNameModal(false)}
          >
            <div className="modal-dialog modal-dialog-centered" role="document" onClick={e => e.stopPropagation()}>
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Редагувати ім'я та прізвище (користувач #{editNameUser.id})</h5>
                  <button type="button" className="close" aria-label="Close" onClick={() => !editNameSaving && setShowEditNameModal(false)}>
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
                <div className="modal-body">
                  <div className="form-group">
                    <label>Ім'я:</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Введіть ім'я"
                      value={editNameName}
                      onChange={e => setEditNameName(e.target.value)}
                      disabled={editNameSaving}
                    />
                  </div>
                  <div className="form-group">
                    <label>Прізвище:</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Введіть прізвище"
                      value={editNameSurname}
                      onChange={e => setEditNameSurname(e.target.value)}
                      disabled={editNameSaving}
                    />
                  </div>
                  {editNameError && (
                    <div className="alert alert-danger mt-2 mb-0" role="alert">
                      {editNameError}
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowEditNameModal(false)} disabled={editNameSaving}>
                    Скасувати
                  </button>
                  <button type="button" className="btn btn-dark" onClick={handleSaveEditName} disabled={editNameSaving}>
                    {editNameSaving ? "Збереження…" : "Зберегти"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <div className="row">
        <div className="col p-0">
          <Footer />
        </div>
      </div>
    </div>
  );
};

export default AdminUsersPage;
